/**
 * Audience Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between Audience TypeScript type and Firestore data.
 *
 * CRITICAL: Uses Client SDK signature - fromFirestore(snapshot, options)
 * NOT Admin SDK signature - fromFirestore(data)
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type { Audience } from '@segmently/schema-models/entities';

export const audienceConverter: FirestoreDataConverter<Audience> = {
  /**
   * Convert Audience model to Firestore data
   * Called when writing to Firestore
   */
  toFirestore(audience: Audience): any {
    const result: any = { ...audience };

    // Convert ISO string dates to Firestore Timestamps
    if (typeof result['creationDate'] === 'string') {
      result['creationDate'] = Timestamp.fromDate(new Date(result['creationDate']));
    } else if (result['creationDate'] instanceof Date) {
      result['creationDate'] = Timestamp.fromDate(result['creationDate']);
    }

    if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = Timestamp.fromDate(new Date(result['updatedAt']));
    } else if (result['updatedAt'] instanceof Date) {
      result['updatedAt'] = Timestamp.fromDate(result['updatedAt']);
    }

    // Remove subcollection field (actors) - not stored in document
    delete result['actors'];

    return result;
  },

  /**
   * Convert Firestore data to Audience model
   * Called when reading from Firestore
   *
   * CRITICAL: Client SDK signature - takes snapshot, not raw data
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): Audience {
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Convert Firestore Timestamps to Date
    if (result['creationDate']?.toDate) {
      result['creationDate'] = result['creationDate'].toDate();
    } else if (typeof result['creationDate'] === 'string') {
      result['creationDate'] = new Date(result['creationDate']);
    }

    if (result['updatedAt']?.toDate) {
      result['updatedAt'] = result['updatedAt'].toDate();
    } else if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = new Date(result['updatedAt']);
    }

    return result as Audience;
  }
};

export default audienceConverter;
